import { doc, updateDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import {
  Opportunity,
  Requirement,
  DraftSection,
  RequiredForm,
  FormatRule,
  SubmissionPackageAudit,
  SubmissionPackageCheck,
  SubmissionCheckStatus,
  ProposalSignOffRecord,
  OfficialSubmissionRecord,
  StageHistoryEntry,
  UserProfile,
} from "../types";
import { sanitizeForFirestore } from "../utils/firestoreUtils";

export interface AuditSubmissionPackageParams {
  opportunity: Opportunity;
  requirements: Requirement[];
  draftSections: DraftSection[];
  forms: RequiredForm[];
  formatRules: FormatRule[];
}

/**
 * Runs a deterministic pre-flight audit across all 5 submission package dimensions:
 * 1. Narrative completeness & zero unresolved placeholders
 * 2. Mandatory forms execution & notarization/signing
 * 3. Standard procurement certifications (W-9, COI, WBE/MBE)
 * 4. Pricing proposal completeness & lock
 * 5. Format & logistics compliance (page budgets, fonts, margins, submission method)
 */
export function auditSubmissionPackage(
  params: AuditSubmissionPackageParams,
): SubmissionPackageAudit {
  const { requirements, draftSections, forms, formatRules } = params;

  const checks: SubmissionPackageCheck[] = [];
  const sectionMap = new Map<string, DraftSection>();
  draftSections.forEach((s) => sectionMap.set(s.requirementId, s));

  // --------------------------------------------------------------------------
  // Dimension 1: Narrative Completeness & Placeholder Audit
  // --------------------------------------------------------------------------
  const mandatoryReqs = requirements.filter((r) => r.mandatory);
  const totalReqsCount = requirements.length;
  const approvedSections = draftSections.filter((s) => s.status === "approved");
  const totalPlaceholders = draftSections.reduce((sum, s) => sum + (s.placeholdersCount || 0), 0);

  let narrativeStatus: SubmissionCheckStatus;
  let narrativeSummary: string;
  const narrativeDetails: string[] = [];

  if (totalReqsCount === 0) {
    narrativeStatus = "blocker";
    narrativeSummary = "No requirements exist for this proposal.";
    narrativeDetails.push(
      "Add or extract requirements before assembling final proposal narrative.",
    );
  } else if (totalPlaceholders > 0) {
    narrativeStatus = "blocker";
    narrativeSummary = `${totalPlaceholders} unresolved [ACTION NEEDED] placeholder(s) detected in drafts.`;
    draftSections
      .filter((s) => (s.placeholdersCount || 0) > 0)
      .slice(0, 5)
      .forEach((s) => {
        narrativeDetails.push(
          `"${s.title}": ${s.placeholdersCount} placeholder(s) require action.`,
        );
      });
  } else {
    const unapprovedMandatory = mandatoryReqs.filter((r) => {
      const draft = sectionMap.get(r.id);
      return !draft || draft.status !== "approved";
    });

    if (unapprovedMandatory.length > 0) {
      narrativeStatus = "blocker";
      narrativeSummary = `${unapprovedMandatory.length} of ${mandatoryReqs.length} mandatory section(s) pending final approval.`;
      unapprovedMandatory.slice(0, 5).forEach((r) => {
        const draft = sectionMap.get(r.id);
        const statusLabel = draft ? draft.status : "unwritten";
        narrativeDetails.push(
          `${r.sectionRef ? r.sectionRef + " " : ""}${r.title} (${statusLabel})`,
        );
      });
    } else {
      const unapprovedTotal = requirements.filter((r) => {
        const draft = sectionMap.get(r.id);
        return !draft || draft.status !== "approved";
      });

      if (unapprovedTotal.length > 0) {
        narrativeStatus = "warning";
        narrativeSummary = `${approvedSections.length} of ${totalReqsCount} sections approved (${unapprovedTotal.length} optional sections in draft/review).`;
        narrativeDetails.push(
          "All mandatory sections are approved. Non-mandatory drafts can be finalized or omitted.",
        );
      } else {
        narrativeStatus = "pass";
        narrativeSummary = `All ${totalReqsCount} proposal section(s) are approved with zero placeholders.`;
      }
    }
  }

  checks.push({
    id: "check_narrative",
    category: "narrative",
    label: "Narrative Completeness & Content Approvals",
    status: narrativeStatus,
    summary: narrativeSummary,
    details: narrativeDetails,
    actionLabel: "Open Drafting Board",
    actionTab: "drafting",
  });

  // --------------------------------------------------------------------------
  // Dimension 2: Mandatory Forms & Wet-Signatures / Notarizations
  // --------------------------------------------------------------------------
  const mandatoryForms = forms.filter((f) => f.mandatory);
  const signedOrAttachedForms = mandatoryForms.filter(
    (f) => f.status === "attached" || f.status === "signed",
  );

  let formsStatus: SubmissionCheckStatus;
  let formsSummary: string;
  const formsDetails: string[] = [];

  if (forms.length === 0) {
    formsStatus = "pass";
    formsSummary = "No mandatory compliance forms were extracted or logged for this solicitation.";
  } else if (mandatoryForms.length === 0) {
    formsStatus = "pass";
    formsSummary = `All ${forms.length} tracked form(s) are optional or informational.`;
  } else if (signedOrAttachedForms.length === mandatoryForms.length) {
    formsStatus = "pass";
    formsSummary = `All ${mandatoryForms.length} mandatory form(s) verified as signed or attached.`;
    const notarizedCount = forms.filter(
      (f) =>
        f.description?.toLowerCase().includes("notar") ||
        f.formName?.toLowerCase().includes("notar"),
    ).length;
    if (notarizedCount > 0) {
      formsDetails.push(`${notarizedCount} form(s) flagged for notary seal verification.`);
    }
  } else {
    const uncompleted = mandatoryForms.filter(
      (f) => f.status !== "attached" && f.status !== "signed",
    );
    formsStatus = "blocker";
    formsSummary = `${uncompleted.length} of ${mandatoryForms.length} mandatory compliance form(s) missing signed copy.`;
    uncompleted.slice(0, 5).forEach((f) => {
      formsDetails.push(`${f.formName} (Status: ${f.status.replace("_", " ")})`);
    });
  }

  checks.push({
    id: "check_forms",
    category: "forms",
    label: "Mandatory Compliance Forms & Signatures",
    status: formsStatus,
    summary: formsSummary,
    details: formsDetails,
    actionLabel: "Verify Forms in Response Plan",
    actionTab: "requirements",
  });

  // --------------------------------------------------------------------------
  // Dimension 3: Standard Certifications & Vault Attachments
  // --------------------------------------------------------------------------
  // Standard enterprise certifications: W-9, Certificate of Insurance (COI), WBE/MBE Diversity
  const certKeywords = ["w-9", "w9", "insurance", "coi", "wbe", "mbe", "diversity", "license"];
  const certForms = forms.filter((f) =>
    certKeywords.some(
      (kw) => f.formName.toLowerCase().includes(kw) || f.description?.toLowerCase().includes(kw),
    ),
  );

  let certStatus: SubmissionCheckStatus;
  let certSummary: string;
  const certDetails: string[] = [];

  if (certForms.length > 0) {
    const missingCerts = certForms.filter((f) => f.status !== "attached" && f.status !== "signed");
    if (missingCerts.length > 0) {
      certStatus = "warning";
      certSummary = `${missingCerts.length} standard certification document(s) pending attachment from Document Vault.`;
      missingCerts.forEach((c) => {
        certDetails.push(`${c.formName}: link latest version from MLC Vault`);
      });
    } else {
      certStatus = "pass";
      certSummary = `All ${certForms.length} standard certification(s) attached and verified current.`;
    }
  } else {
    certStatus = "pass";
    certSummary =
      "Standard certificates (W-9, COI, WBE) ready in MLC Document Vault for submission.";
    certDetails.push(
      "MLC Active Certifications: W-9 (Current Year), COI ($2M Aggregate), WBENC WBE Certified.",
    );
  }

  checks.push({
    id: "check_certifications",
    category: "certifications",
    label: "Certifications & Document Vault Records",
    status: certStatus,
    summary: certSummary,
    details: certDetails,
    actionLabel: "Review Document Vault",
    actionTab: "requirements",
  });

  // --------------------------------------------------------------------------
  // Dimension 4: Pricing Proposal & Cost Breakdown
  // --------------------------------------------------------------------------
  const pricingReqs = requirements.filter((r) => r.category === "pricing");
  let pricingStatus: SubmissionCheckStatus;
  let pricingSummary: string;
  const pricingDetails: string[] = [];

  if (pricingReqs.length === 0) {
    pricingStatus = "warning";
    pricingSummary = "No specific pricing requirements extracted. Verify rate sheet attachment.";
    pricingDetails.push(
      "Ensure rate schedule or cost narrative is verified before final sign-off.",
    );
  } else {
    const unapprovedPricing = pricingReqs.filter((r) => {
      const draft = sectionMap.get(r.id);
      return !draft || draft.status !== "approved";
    });

    if (unapprovedPricing.length > 0) {
      pricingStatus = "blocker";
      pricingSummary = `${unapprovedPricing.length} of ${pricingReqs.length} pricing requirement(s) pending approval.`;
      unapprovedPricing.forEach((r) => {
        pricingDetails.push(`Pricing: ${r.title}`);
      });
    } else {
      pricingStatus = "pass";
      pricingSummary = `All ${pricingReqs.length} pricing requirement(s) approved and cost schedule locked.`;
    }
  }

  checks.push({
    id: "check_pricing",
    category: "pricing",
    label: "Pricing Proposal & Rate Schedule",
    status: pricingStatus,
    summary: pricingSummary,
    details: pricingDetails,
    actionLabel: "Review Pricing Sections",
    actionTab: "drafting",
  });

  // --------------------------------------------------------------------------
  // Dimension 5: Format Rules & Logistics Compliance
  // --------------------------------------------------------------------------
  const totalWords = draftSections.reduce((sum, s) => sum + (s.wordCount || 0), 0);
  const estimatedPages = Math.max(1, Math.ceil(totalWords / 450)); // ~450 words per page standard
  const pageLimitRules = formatRules.filter((r) => r.ruleType === "page_limit");

  let formatStatus: SubmissionCheckStatus;
  let formatSummary: string;
  const formatDetails: string[] = [];

  if (formatRules.length === 0) {
    formatStatus = "pass";
    formatSummary = `Standard MLC formatting applied (~${estimatedPages} estimated pages, ${totalWords.toLocaleString()} words).`;
  } else {
    let limitExceeded = false;
    pageLimitRules.forEach((rule) => {
      // Look for number in rule string
      const match = rule.requirement.match(/(\d+)\s*page/i);
      if (match && match[1]) {
        const maxPages = parseInt(match[1], 10);
        if (estimatedPages > maxPages) {
          limitExceeded = true;
          formatDetails.push(
            `Estimated ${estimatedPages} pages exceeds page limit rule of ${maxPages} pages ("${rule.requirement}")`,
          );
        } else {
          formatDetails.push(`Page budget: ~${estimatedPages} of max ${maxPages} pages utilized.`);
        }
      }
    });

    if (limitExceeded) {
      formatStatus = "blocker";
      formatSummary = `Proposal length (~${estimatedPages} pages) exceeds solicitation page limit rules.`;
    } else {
      formatStatus = "pass";
      formatSummary = `All ${formatRules.length} format rule(s) verified (estimated ~${estimatedPages} pages).`;
    }
  }

  checks.push({
    id: "check_format_rules",
    category: "format_rules",
    label: "Format Constraints & Page Budgets",
    status: formatStatus,
    summary: formatSummary,
    details: formatDetails,
    actionLabel: "View Format Rules",
    actionTab: "requirements",
  });

  // --------------------------------------------------------------------------
  // Score & Aggregation
  // --------------------------------------------------------------------------
  const blockersCount = checks.filter((c) => c.status === "blocker").length;
  const warningsCount = checks.filter((c) => c.status === "warning").length;
  const passedCount = checks.filter((c) => c.status === "pass").length;

  // Weighted score calculation:
  // Narrative: 30%, Forms: 25%, Pricing: 20%, Format: 15%, Certs: 10%
  const weights: Record<string, number> = {
    narrative: 30,
    forms: 25,
    pricing: 20,
    format_rules: 15,
    certifications: 10,
  };

  let overallScore = 0;
  checks.forEach((c) => {
    const w = weights[c.category] || 20;
    if (c.status === "pass") {
      overallScore += w;
    } else if (c.status === "warning") {
      overallScore += w * 0.6;
    }
  });

  overallScore = Math.round(overallScore);

  return {
    overallScore,
    canSubmit: blockersCount === 0,
    blockersCount,
    warningsCount,
    passedCount,
    checks,
    auditedAt: new Date().toISOString(),
  };
}

/**
 * Records a digital sign-off stamp from the Proposal Lead or Executive Decider.
 */
export async function recordProposalSignOff(
  opportunityId: string,
  role: "lead" | "executive",
  user: UserProfile | { displayName?: string | null; email?: string | null },
  comments?: string,
): Promise<ProposalSignOffRecord> {
  const docRef = doc(db, "opportunities", opportunityId);
  const now = new Date().toISOString();

  const record: ProposalSignOffRecord = {
    role,
    signerName: user.displayName || user.email || "Authorized Signer",
    signerEmail: user.email || "",
    signedAt: now,
    comments: comments?.trim() || undefined,
  };

  const fieldToUpdate = role === "lead" ? "leadSignOff" : "executiveSignOff";
  const payload = sanitizeForFirestore({
    [fieldToUpdate]: record,
    updatedAt: now,
  });

  await updateDoc(docRef, payload);
  return record;
}

/**
 * Revokes a proposal sign-off stamp if edits are required before final packaging.
 */
export async function revokeProposalSignOff(
  opportunityId: string,
  role: "lead" | "executive",
): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId);
  const now = new Date().toISOString();
  const fieldToUpdate = role === "lead" ? "leadSignOff" : "executiveSignOff";

  const payload = sanitizeForFirestore({
    [fieldToUpdate]: null,
    updatedAt: now,
  });

  await updateDoc(docRef, payload);
}

export interface RecordOfficialSubmissionParams {
  opportunityId: string;
  submission: Omit<OfficialSubmissionRecord, "submittedAt" | "submittedBy" | "submittedByEmail">;
  user: UserProfile | { displayName?: string | null; email?: string | null };
  currentHistory: StageHistoryEntry[];
}

/**
 * Formally executes the proposal submission:
 * 1. Records official submission metadata (method, portal, confirmation #, receipt)
 * 2. Transitions opportunity stage to "submitted"
 * 3. Logs a stage history audit trail entry
 */
export async function recordOfficialSubmission(
  params: RecordOfficialSubmissionParams,
): Promise<OfficialSubmissionRecord> {
  const { opportunityId, submission, user, currentHistory } = params;
  const docRef = doc(db, "opportunities", opportunityId);
  const now = new Date().toISOString();

  const fullRecord: OfficialSubmissionRecord = {
    submittedAt: now,
    submittedBy: user.displayName || user.email || "Proposal Submitter",
    submittedByEmail: user.email || "",
    method: submission.method,
    portalName: submission.portalName?.trim() || undefined,
    confirmationNumber: submission.confirmationNumber?.trim() || undefined,
    notes: submission.notes?.trim() || undefined,
    receiptStoragePath: submission.receiptStoragePath || undefined,
    receiptDownloadUrl: submission.receiptDownloadUrl || undefined,
    receiptFileName: submission.receiptFileName || undefined,
  };

  const newHistoryEntry: StageHistoryEntry = {
    stage: "submitted",
    changedAt: now,
    changedBy: user.displayName || user.email || "Proposal Submitter",
    changedByEmail: user.email || "",
    note: `Proposal officially submitted via ${submission.method.toUpperCase()}${
      submission.confirmationNumber ? ` (Confirmation #${submission.confirmationNumber})` : ""
    }`,
  };

  const updatedHistory = [...currentHistory, newHistoryEntry];

  const payload = sanitizeForFirestore({
    stage: "submitted",
    officialSubmission: fullRecord,
    stageHistory: updatedHistory,
    updatedAt: now,
  });

  await updateDoc(docRef, payload);
  return fullRecord;
}
